/**
 * Country-aware public signup form controller.
 * All countries use Stripe Pricing Table (switched by billing currency).
 */

import { setButtonLoading } from "../utils/button-loading.js";
import { debounce } from "../utils/debounce.js";
import { formatTaxId } from "../utils/tax-id.js";
import { formatAsYouType } from "../utils/phone.js";
import { buildSignupTaxPayload, shouldLookupBrasilApi, SignupTaxError } from "./signup-tax.js";
import {
  countryDisplayLabel,
  listSignupCountries,
  signupChromeForCountry,
} from "./self-serve-countries.js";
import { createOnboardingIntent, lookupBrasilApiCnpj } from "./signup-api.js";
import { buildSignupIntentBody } from "./signup-intent.js";
import { getSignupState, patchSignupState } from "./signup-state.js";
import {
  applyBrasilApiCompanyData,
  applyCountryChrome,
  clearFormError,
  markSubmitReadyForPlan,
  setSignupProgressStep,
  showFormError,
} from "./signup-form-chrome.js";
import {
  lockSignupPricingTable,
  mountSignupPricingTablePreview,
  unlockSignupPricingTable,
} from "./signup-pricing-table.js";
import { initSignupPlanComparison } from "./signup-plan-comparison.js";
import {
  applySignupSupportLinks,
  applySignupTrialChrome,
  captureSignupAcquisition,
  rememberSignupCurrency,
  renderLandingSelection,
} from "./signup-trial.js";

export { buildSignupIntentBody };

const BRASIL_API_LOOKUP_DEBOUNCE_MS = 300;

export function initSignupFormPage() {
  document.documentElement.classList.remove("dark");
  initSignupPlanComparison();

  const countrySelect = document.getElementById("signupCountry");
  const taxInput = document.getElementById("companyTaxId");
  const phoneInput = document.getElementById("contactPhone");
  const form = document.getElementById("signupForm");
  let trialDays = 0;

  renderLandingSelection(window.location.search);
  const acquisition = captureSignupAcquisition(window.location.search);
  applySignupSupportLinks(window.CONFIG);

  const countries = listSignupCountries();
  if (countrySelect) {
    countrySelect.innerHTML = "";
    countries.forEach((code) => {
      const opt = document.createElement("option");
      opt.value = code;
      opt.textContent = countryDisplayLabel(code);
      countrySelect.appendChild(opt);
    });
    if (!countries.includes(countrySelect.value)) {
      countrySelect.value = countries[0] || "BR";
    }
  }

  const refreshChrome = () => {
    const country = countrySelect?.value || "BR";
    const chrome = signupChromeForCountry(country);
    trialDays = applySignupTrialChrome({
      currency: chrome.currency,
      overrides: window.CONFIG?.SIGNUP_TRIAL_DAYS_BY_CURRENCY,
    });
    applyCountryChrome(chrome, trialDays);
    patchSignupState({
      country,
      lastFetchedTaxId: "",
    });
    const state = getSignupState();
    if (chrome.currency !== state.currency) {
      patchSignupState({ currency: chrome.currency });
      lockSignupPricingTable();
      setSignupProgressStep(1, trialDays);
      mountSignupPricingTablePreview(chrome.currency).catch((err) => {
        window.Logger?.warn?.("Pricing table preview failed to mount", err);
      });
    }
  };

  countrySelect?.addEventListener("change", refreshChrome);
  refreshChrome();

  taxInput?.addEventListener("input", () => {
    const country = countrySelect?.value || "BR";
    const chrome = signupChromeForCountry(country);
    taxInput.value = formatTaxId(taxInput.value, chrome.taxIdType);
  });

  const runBrasilApiLookup = async () => {
    const country = countrySelect?.value || "BR";
    if (!shouldLookupBrasilApi(country) || !taxInput) return;
    const chrome = signupChromeForCountry(country);
    let cleaned;
    try {
      cleaned = buildSignupTaxPayload({
        country,
        taxId: taxInput.value,
      }).tax_id;
    } catch {
      return;
    }
    const state = getSignupState();
    if (cleaned === state.lastFetchedTaxId) return;
    patchSignupState({ lastFetchedTaxId: cleaned });
    const data = await lookupBrasilApiCnpj(cleaned);
    if (!data) {
      patchSignupState({ lastFetchedTaxId: "" });
      return;
    }
    applyBrasilApiCompanyData(data, chrome.phoneRegion);
  };

  const debouncedBrasilApiLookup = debounce(runBrasilApiLookup, BRASIL_API_LOOKUP_DEBOUNCE_MS);
  taxInput?.addEventListener("blur", () => {
    debouncedBrasilApiLookup();
  });

  phoneInput?.addEventListener("input", () => {
    const country = countrySelect?.value || "BR";
    const chrome = signupChromeForCountry(country);
    phoneInput.value = formatAsYouType(phoneInput.value, chrome.phoneRegion);
  });

  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const submitButton = document.getElementById("signupSubmitButton");
    clearFormError();

    const country = countrySelect?.value || "BR";
    const chrome = signupChromeForCountry(country);

    let intentBody;
    try {
      intentBody = buildSignupIntentBody({
        country,
        taxId: taxInput?.value || "",
        responsibleName: document.getElementById("companyName")?.value || "",
        contactEmail: document.getElementById("contactEmail")?.value || "",
        contactPhone: phoneInput?.value || "",
        acquisition,
      });
    } catch (err) {
      showFormError(
        err instanceof SignupTaxError
          ? err.message
          : err.message || "Dados inválidos."
      );
      return;
    }

    setButtonLoading(submitButton, true, "Registrando dados...");

    try {
      const onboardingId = await createOnboardingIntent(intentBody);
      const contactEmail = String(intentBody.contact_email || "");
      patchSignupState({ onboardingId, contactEmail });

      const mounted = await mountSignupPricingTablePreview(chrome.currency, {
        onboardingId,
        contactEmail,
      });
      if (!mounted) {
        throw new Error(
          "Tabela de planos indisponível para este país. Tente novamente em breve."
        );
      }
      const unlocked = unlockSignupPricingTable(onboardingId, contactEmail);
      if (!unlocked) {
        throw new Error("Não foi possível liberar a tabela de planos.");
      }
      rememberSignupCurrency(chrome.currency);
      setSignupProgressStep(2, trialDays);
      document
        .getElementById("signupPricingTableHost")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      markSubmitReadyForPlan(submitButton, trialDays);
    } catch (error) {
      showFormError(error.message || "Falha ao preparar o pagamento.");
      window.Logger?.error?.("Signup onboarding intent error", error);
      setButtonLoading(submitButton, false);
    }
  });
}
